/**
 * Running installed scripts against the bars on screen.
 *
 * One `ScriptRunner` — and therefore one Worker — per installed script, kept
 * for as long as the script is installed. A Worker per RUN would recompile the
 * body on every tick; a single Worker shared by every script would let one
 * script's runaway loop stop every other study on the chart, because runs
 * inside a thread are serial and the fix for a runaway loop is to terminate the
 * thread.
 *
 * ## Colours come from the theme, by index
 *
 * A script picks `colorIndex`, not a colour. This is the array it indexes into,
 * and every entry is a semantic token — so a shared script cannot draw itself
 * invisible against the background or in the same colour as the price line, and
 * a redesign carries every installed script with it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MAX_SCRIPT_BARS, buildScriptPayload, resolveScriptInputs,
  type Bar, type ScriptInputDecl, type ScriptInstrumentSource, type ScriptPlotDecl,
  type ScriptSeries,
} from '@aegis/core';
import type { InstalledScriptDto } from '@aegis/api-client';
import type { SemanticToken } from '@aegis/ui';
import { useScripts } from './scripts.js';
import { createScriptRunner, type ScriptRunner, type ScriptWorkerFactory } from './scriptRunner.js';

/**
 * The eight colours a script may choose between.
 *
 * Ordered so that the low indices are the ones a single-plot study will pick,
 * and none of them is the price series' own colour or the background.
 */
export const SCRIPT_PALETTE: readonly SemanticToken[] = [
  'accent', 'aiPrimary', 'brandStrong', 'statusCaution',
  'statusWarning', 'marketUp', 'marketDown', 'textSecondary',
];

/**
 * How often a study may re-run while the chart is ticking.
 *
 * The work is off the main thread, so this is about the trader's battery and
 * their other cores rather than about the interface staying responsive. Half a
 * second is below the point at which a redrawn line reads as lagging.
 */
const THROTTLE_MS = 500;

export interface StudyPlot {
  /** `scriptId:plotId`. Unique across studies, and stable across re-runs. */
  readonly key: string;
  readonly label: string;
  readonly style: ScriptPlotDecl['style'];
  readonly colorIndex: number;
  readonly lineWidth: number;
  /** True to draw on the price pane, false for a pane of its own. */
  readonly overlay: boolean;
  /** Epoch seconds to value. `null` is a gap the chart leaves empty. */
  readonly points: readonly { readonly time: number; readonly value: number | null }[];
}

export interface Study {
  readonly script: InstalledScriptDto;
  readonly state: 'running' | 'ok' | 'error' | 'timeout' | 'disabled' | 'refused';
  readonly message?: string;
  readonly plots: readonly StudyPlot[];
  /** Bars where the script produced no value. Warm-up is the usual reason. */
  readonly gaps: number;
}

const defaultFactory: ScriptWorkerFactory = () => new Worker(
  new URL('../workers/indicator.worker.ts', import.meta.url),
  { type: 'module' },
) as unknown as ReturnType<ScriptWorkerFactory>;

export interface UseStudiesOptions {
  /** Which chart is asking. Scopes what this pane's run states overwrite. */
  readonly paneId: string;
  readonly bars: readonly Bar[];
  /**
   * The contract, in whatever form the caller holds it.
   *
   * Narrow on purpose — see `ScriptInstrumentSource`. The web app's instrument
   * is the API's DTO, with prices as strings; the server's is the master, with
   * exact decimals. Both satisfy this, and neither can carry anything else
   * across the boundary by being wider than it needs to be.
   */
  readonly instrument: ScriptInstrumentSource | null;
  /** Injectable so a test can drive the runner without a real Worker. */
  readonly workerFactory?: ScriptWorkerFactory;
}

export function useStudies({
  paneId, bars, instrument, workerFactory,
}: UseStudiesOptions): readonly Study[] {
  const { scripts, report } = useScripts();
  const [results, setResults] = useState<Record<string, Study>>({});

  const runners = useRef(new Map<string, ScriptRunner>());
  const factory = workerFactory ?? defaultFactory;

  /** Installed, switched on, and not taken down. */
  const active = useMemo(
    () => (scripts?.installed ?? []).filter((s) => s.enabled),
    [scripts],
  );

  /* Dispose the runner for any script that is no longer active. A Worker left
     behind is a thread doing nothing, holding a compiled copy of code the
     trader has removed. */
  useEffect(() => {
    const live = new Set(active.map((s) => s.id));
    for (const [id, runner] of runners.current) {
      if (!live.has(id)) {
        runner.dispose();
        runners.current.delete(id);
      }
    }
  }, [active]);

  const disposeAll = runners.current;
  useEffect(() => () => {
    for (const runner of disposeAll.values()) runner.dispose();
    disposeAll.clear();
  }, [disposeAll]);

  /**
   * A key that changes exactly when a re-run would draw something different.
   *
   * The bar ARRAY is new on every tick — `bars !== bars` across renders is not
   * a reason to run a script. What matters is the count, the window, and the
   * forming bar's close.
   */
  const barKey = bars.length === 0
    ? 'empty'
    : `${bars.length}:${bars[0]!.time}:${bars[bars.length - 1]!.time}:${bars[bars.length - 1]!.close}`;

  const settingsKey = useMemo(
    () => active.map((s) => `${s.id}:${s.fingerprint}:${JSON.stringify(s.settings)}`).join('|'),
    [active],
  );

  const barsRef = useRef(bars);
  barsRef.current = bars;
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!instrument || bars.length === 0 || active.length === 0) return undefined;

    let cancelled = false;

    const runAll = (): void => {
      for (const script of activeRef.current) {
        let runner = runners.current.get(script.id);
        if (!runner) {
          runner = createScriptRunner(factory);
          runners.current.set(script.id, runner);
        }

        const inputs = resolveScriptInputs(
          script.inputs as readonly ScriptInputDecl[],
          script.settings as Record<string, unknown>,
        );
        const used = barsRef.current.length > MAX_SCRIPT_BARS
          ? barsRef.current.slice(-MAX_SCRIPT_BARS)
          : barsRef.current;
        const payload = buildScriptPayload(used, instrument, inputs);

        void runner.run({
          source: script.source,
          /* The fingerprint the TRADER reviewed, not the one that arrived with
             the script. An author who edited a shared script after it was
             installed is refused here, by name. */
          fingerprint: script.fingerprintAtInstall,
          status: script.status,
          plots: script.plots as readonly ScriptPlotDecl[],
          payload,
        }).then((outcome) => {
          if (cancelled || outcome.status === 'superseded') return;
          setResults((prev) => ({
            ...prev,
            [script.id]: toStudy(script, outcome, used),
          }));
        });
      }
    };

    /* Trailing throttle. The first run of a new configuration is immediate —
       a trader who just changed a length wants to see it — and the ticking
       chart is what gets rate-limited. */
    runAll();
    const handle = window.setInterval(runAll, THROTTLE_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
    // `barKey` is the dependency, not `bars`: see its definition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrument, settingsKey, barKey === 'empty', factory]);

  const studies = useMemo(
    () => active.map((s) => results[s.id] ?? {
      script: s, state: 'running' as const, plots: [], gaps: 0,
    }),
    [active, results],
  );

  /* Tell the panel how each one went. The panel has no bars, so this is the
     only place the answer exists. */
  useEffect(() => {
    report(paneId, Object.fromEntries(studies.map((s) => [s.script.id, {
      state: s.state, ...(s.message ? { message: s.message } : {}), gaps: s.gaps,
    }])));
  }, [paneId, studies, report]);

  return studies;
}

function toStudy(
  script: InstalledScriptDto,
  outcome: Awaited<ReturnType<ScriptRunner['run']>>,
  bars: readonly Bar[],
): Study {
  if (outcome.status !== 'ok') {
    return {
      script,
      state: outcome.status === 'superseded' ? 'running' : outcome.status,
      ...(outcome.status === 'superseded' ? {} : { message: outcome.message }),
      plots: [],
      gaps: 0,
    };
  }

  return {
    script,
    state: 'ok',
    gaps: outcome.output.gaps,
    plots: (script.plots as readonly ScriptPlotDecl[]).map((p) => ({
      key: `${script.id}:${p.id}`,
      label: p.label,
      style: p.style,
      colorIndex: p.colorIndex,
      lineWidth: p.lineWidth,
      overlay: script.overlay,
      points: pointsOf(outcome.output.series, p.id, bars),
    })),
  };
}

/**
 * Pair a plot's values with the bars they were computed from.
 *
 * The values are positional — index `i` belongs to bar `i` — which is why
 * `validateScriptOutput` refuses a series of the wrong length rather than
 * padding it. A padded series would line up here without complaint and draw
 * every value one bar away from where it happened.
 */
function pointsOf(
  series: ScriptSeries, plotId: string, bars: readonly Bar[],
): readonly { time: number; value: number | null }[] {
  const values = series[plotId] ?? [];
  return bars.map((b, i) => ({ time: b.time, value: values[i] ?? null }));
}
